import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ModalPopup } from "../../common";
import FeedbackRequestDto from "../../../types/feedback/FeedbackRequestDto";
import feedbackRequestApi from "../../../api/feedback/feedbackRequestApi";
import AppRoutes from "../../../routes/AppRoutes";
import { MultipleChoiceOptionStringId } from "../../../types/forms/MultipleChoiceOptions";
import { buildDeclineOptions } from "./shared/feedbackDeclineOptions";
import {
  FeedbackDeclineStepBody,
  FeedbackDeclineStepFooter,
} from "./shared/FeedbackDeclineStep";
import FeedbackRequestMessageQuote from "./shared/FeedbackRequestMessageQuote";
import useFeedbackDeclineSubmit from "./shared/useFeedbackDeclineSubmit";

type ModalStep = "intro" | "decline";

interface FeedbackRecipientIntroModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  feedbackRequest: FeedbackRequestDto | null;
  api: feedbackRequestApi;
}

function FeedbackRecipientIntroModal({
  isOpen,
  onDismiss,
  feedbackRequest,
  api,
}: FeedbackRecipientIntroModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<ModalStep>("intro");
  const [declineOptions, setDeclineOptions] = useState<
    MultipleChoiceOptionStringId[]
  >(() => buildDeclineOptions(t));

  const resetIntroState = useCallback(() => {
    setStep("intro");
    setDeclineOptions(buildDeclineOptions(t));
  }, [t]);

  const { isSaving, saveError, submitDecline, clearSaveError } =
    useFeedbackDeclineSubmit({
      api,
      requestId: feedbackRequest?.id,
      onSaved: () => {
        resetIntroState();
        clearSaveError();
        onDismiss();
        navigate(AppRoutes.yourJourney.root);
      },
      onWillGive: () => {
        resetIntroState();
        clearSaveError();
        onDismiss();
      },
    });

  useEffect(() => {
    if (!isOpen) {
      resetIntroState();
      clearSaveError();
    }
  }, [isOpen, resetIntroState, clearSaveError]);

  const handleDismiss = () => {
    resetIntroState();
    clearSaveError();
    onDismiss();
  };

  const requesterName =
    feedbackRequest?.requesterName ??
    t("Common.Someone", { defaultValue: "Someone" });
  const requestMessage = feedbackRequest?.message?.trim() ?? "";

  const handleDeclineClick = () => {
    setDeclineOptions(buildDeclineOptions(t));
    clearSaveError();
    setStep("decline");
  };

  const handleBackToIntro = () => {
    setStep("intro");
    clearSaveError();
  };

  const introBody = (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 leading-relaxed">
        {t("Feedback.RecipientIntro.InviteBody", {
          requesterName,
          defaultValue:
            "{{requesterName}} has invited you to provide feedback.",
        })}
      </p>
      {requestMessage && (
        <div className="text-sm text-gray-600 leading-relaxed">
          <p>
            {t("Feedback.RecipientIntro.MessageLabel", {
              defaultValue: "Message:",
            })}
          </p>
          <FeedbackRequestMessageQuote
            message={requestMessage}
            className="mt-1 rounded-r-md border-l-2 border-l-gray-300 bg-gray-50/90 px-3 py-2.5 text-gray-600 whitespace-pre-wrap break-words"
          />
        </div>
      )}
      <p className="text-sm text-gray-600 leading-relaxed">
        {t("Feedback.RecipientIntro.InstructionsBody", {
          defaultValue:
            "Use the space at the bottom to share anything you think would be helpful, then submit your response when you're ready.",
        })}
      </p>
      <p className="text-sm text-gray-600 leading-relaxed">
        {t("Feedback.RecipientIntro.ReadOnlyNote", {
          defaultValue:
            "Please note: you cannot change any answers in the document",
        })}
      </p>
    </div>
  );

  return (
    <ModalPopup
      isOpen={isOpen}
      onOpenChange={() => {}}
      title={t("Feedback.RecipientIntro.Title", {
        defaultValue: "Give your feedback",
      })}
      onPrimaryButtonClick={step === "intro" ? handleDismiss : undefined}
      primaryButtonText={
        step === "intro" ? t("Common.OK", { defaultValue: "OK" }) : undefined
      }
      onSecondaryButtonClick={step === "intro" ? handleDeclineClick : undefined}
      secondaryButtonText={
        step === "intro"
          ? t("Feedback.RecipientIntro.DeclineCta", {
              defaultValue: "I'd prefer not to give feedback",
            })
          : undefined
      }
      footerComponent={
        step === "decline" ? (
          <FeedbackDeclineStepFooter
            declineOptions={declineOptions}
            onBack={handleBackToIntro}
            onSubmit={() => submitDecline(declineOptions)}
            isSaving={isSaving}
          />
        ) : undefined
      }
      isLoading={isSaving}
      width="TINY"
      showCloseIcon={!isSaving}
      preventInitialAutoFocus
      onAlternativeOpenChange={handleDismiss}
    >
      {step === "intro" ? (
        introBody
      ) : (
        <FeedbackDeclineStepBody
          declineOptions={declineOptions}
          onChange={setDeclineOptions}
          saveError={saveError}
          uniqueFieldName="feedback-decline-reason-collab"
        />
      )}
    </ModalPopup>
  );
}

export default FeedbackRecipientIntroModal;
